import React from 'react';
import ReactDOM from 'react-dom/client';
// Sentry must initialize before App's module graph evaluates: App.tsx calls
// Sentry.withSentryReactRouterV6Routing at module scope, which silently
// returns the unwrapped Routes if the routing integration hasn't been set up.
import './utils/Instrument';
// antd v5's static APIs (Modal.confirm, message, notification) call the removed ReactDOM.render
// under React 19 and silently no-op without this official compatibility patch.
import '@ant-design/v5-patch-for-react-19';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { Auth0Provider, CacheLocation } from '@auth0/auth0-react';
import { Toaster } from 'react-hot-toast';

import { safeSessionSetItem, safeSetItem } from './shared/safeStorage';
import { persistEtid } from './shared/etid';
import { persistPartnerCode } from './shared/partnerCode';

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

const onRedirectCallback = (appState: any) => {
  // Get the return path from appState (Auth0 preserved it!)
  const targetPath = appState?.returnTo;
  const tachref = appState?.tachref;
  const tachRole = appState?.tachRole;

  if (targetPath) {
    // Store it in sessionStorage immediately so AuthRouter can use it
    safeSessionSetItem('redirectPath', targetPath);
  }

  // Restore tachref if it was preserved through Auth0
  if (tachref) {
    safeSetItem('tachref', tachref);
  }

  // Store tachRole if it was preserved through Auth0
  if (tachRole) {
    // Store in localStorage for persistence (similar to tachref)
    safeSetItem('tachRole', tachRole);
    // Also store in sessionStorage for immediate use
    safeSessionSetItem('tachRole', tachRole);
  }

  // Restore etid if it was preserved through Auth0. persistEtid (not a bare
  // safeSetItem) so the `.tachusa.com` cookie is written alongside the
  // localStorage mirror — otherwise attribution rides a single channel and is
  // dropped for anyone whose localStorage is blocked or full.
  const etid = appState?.etid;
  if (etid) {
    persistEtid(etid);
  }

  // Restore the co-branded partner code if it was preserved through Auth0.
  // persistPartnerCode ignores a missing or malformed value.
  persistPartnerCode(appState?.partner);

  // Navigate to /login so AuthRouter can handle the redirect properly
  // This ensures AuthRouter sees the authenticated state and stored redirectPath
  //
  // NOTE: for a visitor with site data blocked, the sessionStorage write above
  // no-ops and this call drops the only other copy, so the destination is lost.
  // A `?returnTo=` fallback here does NOT work: React Router never observes a
  // raw replaceState (its listener is wired to popstate alone), so both
  // consumers read a stale `useLocation().search` and see nothing. Fixing it
  // properly belongs with the blocked-sign-in work, where the whole path is
  // reachable end-to-end and can actually be verified.
  window.history.replaceState({}, document.title, '/login');
};

const domainEnv = process.env.REACT_APP_AUTH0_DOMAIN;
const clientIdEnv = process.env.REACT_APP_AUTH0_CLIENT_ID;
const audienceEnv = process.env.REACT_APP_AUTH0_AUDIENCE;

export function getConfig() {
  // Configure the audience here. By default, it will take whatever is in the config
  // (specified by the `audience` key) unless it's the default value of "YOUR_API_IDENTIFIER" (which
  // is what you get sometimes by using the Auth0 sample download tool from the quickstart page, if you
  // don't have an API).
  // If this resolves to `null`, the API page changes to show some helpful info about what to do
  // with the audience.
  const audience =
    audienceEnv && audienceEnv !== 'YOUR_API_IDENTIFIER' ? audienceEnv : null;

  return {
    domain: domainEnv,
    clientId: clientIdEnv,
    ...(audience ? { audience } : null),
  };
}

const config = getConfig();
const providerConfig = {
  domain: config.domain || '',
  clientId: config.clientId || '',
  onRedirectCallback,
  authorizationParams: {
    redirect_uri: window.location.origin + '/login',
    ...(config.audience ? { audience: config.audience } : null),
  },
  cacheLocation: 'memory' as CacheLocation,
};

root.render(
  // <React.StrictMode>
  <Auth0Provider {...providerConfig}>
    <App />
    <Toaster />
  </Auth0Provider>
  // </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
